import {
  Card,
  Control,
  ControlResponse,
  Language,
  Notification,
  Operation,
  Operator,
} from '@yleisradio/areena-types';
import { useState } from 'react';
import { apmRum } from 'services/apm-rum';
import { executeOperation } from 'services/areena-api/fetchers';
import logger from 'services/logger';
import { OperationState } from 'components/Controls/Control/type';
import { ErrorWithNotifications } from 'utils/errorWithNotifications';
import { toast } from 'components/Notifications';
import { getTranslation } from 'services/translations';
import { useUILanguage } from 'hooks/useUILanguage';
import { Action } from './type';
import {
  isFavorite,
  isMarkDeleted,
  isMarkFinished,
  isOperator,
  isRating,
} from 'components/Controls/Control/typeGuards';
import { KeyedMutator } from 'swr';
import { isCard } from 'utils/card';

// Card = replace source card, null = remove source card
export type OnCardUpdate = (card: Card | null) => void;

// Response data is a card, a control or empty (= card removed)
function getCardUpdate(
  response: ControlResponse | null
): Card | null | undefined {
  const data: unknown = response?.data;
  if (isCard(data)) return data;
  if (!data) return null;
  return undefined;
}

type ControlErrorMessage = {
  notification: string;
  helpText: string;
};

const getOperationErrorNotification = (
  operation: Operation,
  language: Language
): ControlErrorMessage => {
  if (operation.method === 'DELETE') {
    return {
      notification: getTranslation(language, 'notificationRemoveFailed'),
      helpText: getTranslation(language, 'notificationHelpTextGeneric'),
    };
  } else {
    return {
      notification: getTranslation(language, 'genericError'),
      helpText: getTranslation(language, 'notificationHelpTextGeneric'),
    };
  }
};

function getUIMessage(
  notifications: Notification[] | undefined
): string | undefined {
  if (notifications) {
    const uiMessages = notifications.filter((n) => !!n.uiMessage);
    if (uiMessages.length > 0) {
      return uiMessages.reduce(
        (message, { uiMessage }) =>
          `${message}${message ? ', ' : ''}${uiMessage}`,
        ''
      );
    }
  }
}

function handleFavoriteSuccessNotification(
  operation: Operation,
  language: Language
) {
  const message =
    operation.method === 'DELETE'
      ? getTranslation(language, 'notificationFavoriteRemoved')
      : getTranslation(language, 'notificationFavoriteAdded');

  toast(message, 'primary', undefined, 5000, 'success');
}

// DELETE undoes the rating
function handleRatingSuccessNotification(
  control: Operator,
  language: Language
) {
  const isUndo = control.operation.method === 'DELETE';
  const message = getTranslation(
    language,
    isUndo ? 'notificationRatingRemoved' : 'notificationRatingAdded'
  );
  const thumb = control.tag === 'rating_up' ? 'thumbs-up' : 'thumbs-down';

  toast(
    message,
    'primary',
    undefined,
    5000,
    isUndo ? thumb : `${thumb}-active`
  );
}

// DELETE marks the program as unfinished
function handleMarkFinishedSuccessNotification(
  operation: Operation,
  language: Language
) {
  const message = getTranslation(
    language,
    operation.method === 'DELETE'
      ? 'notificationMarkedUnfinished'
      : 'notificationMarkedFinished'
  );

  toast(message, 'primary', undefined, 5000, 'success');
}

function handleOperationFailedNotification(
  operation: Operation,
  language: Language,
  error: unknown
) {
  const messageFromApi =
    error instanceof ErrorWithNotifications
      ? getUIMessage(error.notifications)
      : null;

  if (messageFromApi) {
    toast(messageFromApi, 'error');
  } else {
    const { notification, helpText } = getOperationErrorNotification(
      operation,
      language
    );

    toast(notification, 'error', helpText);
  }
}

type Props = {
  control: Control | null;
  setOperationState: ((newState: OperationState) => void) | undefined;
  mutateControl: KeyedMutator<ControlResponse | null>;
  onCardUpdate?: OnCardUpdate | undefined;
};

export function useOperatorAction({
  control,
  setOperationState,
  mutateControl,
  onCardUpdate,
}: Props): Action {
  const [isExecuting, setIsExecuting] = useState(false);
  const language = useUILanguage();

  async function operationAction() {
    if (!isOperator(control)) {
      return;
    }

    if (isExecuting) {
      return;
    }

    const { operation } = control;

    setIsExecuting(true);

    try {
      const response = await executeOperation(operation);
      setOperationState?.('complete');
      const cardUpdate = getCardUpdate(response);
      if (onCardUpdate && cardUpdate !== undefined) {
        onCardUpdate(cardUpdate);
      } else {
        mutateControl(response || undefined, {
          revalidate: false,
        });
      }

      if (isFavorite(control)) {
        handleFavoriteSuccessNotification(operation, language);
      } else if (isRating(control)) {
        handleRatingSuccessNotification(control, language);
      } else if (isMarkFinished(control)) {
        handleMarkFinishedSuccessNotification(operation, language);
      } else if (isMarkDeleted(control)) {
        toast(
          getTranslation(language, 'toast_continue_list_removed'),
          'primary',
          undefined,
          5000,
          'success'
        );
      }
    } catch (e) {
      logger.error(e);
      if (apmRum && e instanceof Error) {
        apmRum.captureError(e);
      }

      setOperationState?.('failed');
      mutateControl();
      handleOperationFailedNotification(operation, language, e);
    } finally {
      setIsExecuting(false);
    }
  }

  return {
    runAction: operationAction,
    executing: isExecuting,
  };
}
